import { useHead } from '@unhead/vue'

const SITE = 'https://www.papersnap.io'
const BRAND = 'Papersnap'
const DEFAULT_TITLE = 'Snap a document. Get your data.'
const DEFAULT_DESC =
  'Upload an invoice, receipt, purchase order, utility bill or financial report. Get clean structured data — JSON or CSV — in under 15 seconds.'
const DEFAULT_OG_IMAGE = `${SITE}/og-image.png`

export function faqJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

export function articleJsonLd({ headline, description, url, datePublished = '2026-07-08' }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    url,
    datePublished,
    dateModified: datePublished,
    author: { '@type': 'Organization', name: BRAND, url: SITE },
    publisher: { '@type': 'Organization', name: 'Keelara LLC' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  }
}

// items: [{ name, url }] in order (Home first); URLs absolute.
export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  }
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND,
    url: SITE,
    description:
      'AI document intelligence — extract structured data from invoices, receipts, and financial documents.',
    parentOrganization: { '@type': 'Organization', name: 'Keelara LLC' },
  }
}

export function softwareAppJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: BRAND,
    url: SITE,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Extract structured data (JSON/CSV) from invoices, receipts, purchase orders, utility bills, and financial reports in seconds.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free for 5 pages/month; paid plans from $9/mo.',
    },
    author: { '@type': 'Organization', name: 'Keelara LLC' },
  }
}

/**
 * Per-view SEO. Sets a unique title, description, and — critically — a
 * self-referential canonical + og:url, plus optional JSON-LD. Without this the
 * prerendered routes all inherit index.html's homepage canonical.
 */
export function usePageSeo({ title, description, path, robots, jsonLd, image } = {}) {
  // App.vue registers a global titleTemplate that appends " | Papersnap" (and
  // supplies the homepage default when title is empty), so pass the BARE title.
  const fullTitle = title ? `${title} | ${BRAND}` : DEFAULT_TITLE
  const fullDesc = description || DEFAULT_DESC
  const url = `${SITE}${path || ''}`
  const ogImage = image || DEFAULT_OG_IMAGE
  const ldBlocks = (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).filter(Boolean)

  useHead({
    title: title || undefined,
    script: ldBlocks.map((block) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(block),
    })),
    meta: [
      { name: 'description', content: fullDesc },
      ...(robots ? [{ name: 'robots', content: robots }] : []),
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: fullDesc },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: BRAND },
      { property: 'og:image', content: ogImage },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: fullDesc },
      { name: 'twitter:image', content: ogImage },
    ],
    link: [{ rel: 'canonical', href: url }],
  })
}
